import { createSlice } from "@reduxjs/toolkit";
import {
  fetchRoomsAction,
  createRoomAction,
  joinMeetingAction,
  checkJoinStatusAction,
  syncMeetingStateAction,
  completeRoomAction,
  adminJoinMeetingAction,
  getWaitingParticipantsAction,
  approveParticipantAction,
  removeParticipantAction,
  leaveMeetingAction,
} from "../../actions/room/roomActions";

const roomSlice = createSlice({
  name: "room",
  initialState: {
    rooms: [],
    loading: false,
    error: null,
    currentRoom: null,
    waitingParticipants: [],
    joinStatus: null,
    socketStatus: null,
    lastEventId: 0,
  },
  reducers: {
    setRooms(state, action) {
      state.rooms = action.payload;
    },
    setCurrentRoom(state, action) {
      state.currentRoom = action.payload;
    },
    clearRoomError(state) {
      state.error = null;
    },
    setSocketStatus(state, action) {
      state.socketStatus = action.payload;
    },
    onAdmissionChanged(state, action) {
      const evt = action.payload;
      if (evt?.eventId && evt.eventId > state.lastEventId) {
        state.lastEventId = evt.eventId;
      }
      if (evt?.participant) {
        state.socketStatus = {
          status: evt.participant.status,
          participant: evt.participant,
          type: evt.type,
        };
      }
    },
    onParticipantChanged(state, action) {
      const evt = action.payload;
      if (evt?.eventId && evt.eventId > state.lastEventId) {
        state.lastEventId = evt.eventId;
      }
      const participant = evt?.participant;
      if (!participant) return;
      const list = state.waitingParticipants;
      const idx = list.findIndex(
        (p) =>
          p.identity === participant.identity ||
          p.phoneNumber === participant.phoneNumber,
      );
      if (
        participant.status === "pending" ||
        participant.status === "approved"
      ) {
        if (idx >= 0) {
          list[idx] = { ...list[idx], ...participant };
        } else {
          list.push(participant);
        }
      } else {
        if (idx >= 0) list.splice(idx, 1);
      }
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchRoomsAction.pending, (state, action) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoomsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.rooms = action.payload?.rooms || [];
        return state;
      })
      .addCase(fetchRoomsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createRoomAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(createRoomAction.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRoom = action.payload;
        return state;
      })
      .addCase(createRoomAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(joinMeetingAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(joinMeetingAction.fulfilled, (state, action) => {
        state.loading = false;
        return state;
      })
      .addCase(joinMeetingAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(checkJoinStatusAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(checkJoinStatusAction.fulfilled, (state, action) => {
        state.loading = false;
        state.joinStatus = action.payload;
        return state;
      })
      .addCase(checkJoinStatusAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(syncMeetingStateAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(syncMeetingStateAction.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        state.waitingParticipants = data?.participants || [];
        state.socketStatus =
          data?.self || state.socketStatus || null;
        if (data?.events?.length) {
          const maxId = data.events.reduce(
            (m, e) => Math.max(m, e.eventId || 0),
            state.lastEventId,
          );
          state.lastEventId = Math.max(state.lastEventId, maxId);
        }
        return state;
      })
      .addCase(syncMeetingStateAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(completeRoomAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(completeRoomAction.fulfilled, (state, action) => {
        state.loading = false;
        return state;
      })
      .addCase(completeRoomAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(adminJoinMeetingAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(adminJoinMeetingAction.fulfilled, (state, action) => {
        state.loading = false;
        return state;
      })
      .addCase(adminJoinMeetingAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getWaitingParticipantsAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getWaitingParticipantsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.waitingParticipants = action.payload?.participants || [];
        return state;
      })
      .addCase(getWaitingParticipantsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(approveParticipantAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(approveParticipantAction.fulfilled, (state, action) => {
        state.loading = false;
        return state;
      })
      .addCase(approveParticipantAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(removeParticipantAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(removeParticipantAction.fulfilled, (state, action) => {
        state.loading = false;
        return state;
      })
      .addCase(removeParticipantAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(leaveMeetingAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(leaveMeetingAction.fulfilled, (state, action) => {
        state.loading = false;
        return state;
      })
      .addCase(leaveMeetingAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      }),
});

export const {
  setRooms,
  setCurrentRoom,
  clearRoomError,
  setSocketStatus,
  onAdmissionChanged,
  onParticipantChanged,
} = roomSlice.actions;
export default roomSlice.reducer;
